import Component from "@glimmer/component";
import { action } from "@ember/object";
import { tracked } from "@glimmer/tracking";
import { hash, fn } from "@ember/helper";
import ComboBox from "discourse/select-kit/components/combo-box";
import MultiSelect from "discourse/select-kit/components/multi-select";
import Group from "discourse/models/group";
import I18n, { i18n } from "discourse-i18n";

export default class WazeFormsSettings extends Component {
  @tracked groups = [];
  @tracked isWazeFormsEnabled = false;
  @tracked isOpenLogsButtonEnabled = false;

  constructor(...args) {
    super(...args);
    Group.findAll().then((groups) => {
      this.groups = groups.map((group) => group.name);
    });

    const customFields =
      this.args.outletArgs?.transientData?.custom_fields || {};
    this.isWazeFormsEnabled =
      customFields.enable_waze_forms?.toString() === "true" ||
      customFields.enable_waze_forms === true;
    this.isOpenLogsButtonEnabled =
      customFields.enable_open_logs_button?.toString() === "true" ||
      customFields.enable_open_logs_button === true;
  }

  get wazeFormsComposeButtonOptions() {
    const labels =
      I18n.translations[I18n.locale]?.js?.waze?.forms?.create_topic_label ??
      I18n.translations.en?.js?.waze?.forms?.create_topic_label ??
      {};
    return Object.keys(labels).map((id) => ({
      id,
      name: i18n(`waze.forms.create_topic_label.${id}`) || labels[id],
    }));
  }

  @action
  async toggleEnableWazeForms(value, { set, name }) {
    this.isWazeFormsEnabled = value;
    await set(name, value ? "true" : "false");
  }

  @action
  async toggleEnableOpenLogsButton(value, { set, name }) {
    this.isOpenLogsButtonEnabled = value;
    await set(name, value ? "true" : "false");
  }

  get groupsAllowedToOpenLogs() {
    const currentGroups =
      this.args.outletArgs?.transientData?.custom_fields
        ?.groups_allowed_to_open_logs;
    return (currentGroups || "").split("|").filter(Boolean);
  }

  @action
  changeGroupsAllowedToOpenLogs(field, newGroups) {
    field.set(newGroups.join("|"));
  }

  <template>
    {{#let @outletArgs.form as |form|}}
      <form.Section
        @title={{i18n "waze.forms.settings_section_title"}}
        class="category-custom-settings-outlet waze-forms-settings"
      >
        <form.Object @name="custom_fields" as |customFields|>
          <customFields.Field
            @name="enable_waze_forms"
            @type="checkbox"
            @format="max"
            @title={{i18n "waze.forms.enable_waze_forms"}}
            @onSet={{this.toggleEnableWazeForms}}
            as |field|
          >
            <field.Control checked={{this.isWazeFormsEnabled}} />
          </customFields.Field>

          {{#if this.isWazeFormsEnabled}}
            <customFields.Field
              @name="waze_forms_form_id"
              @type="input"
              @format="max"
              @title={{i18n "waze.forms.form_id"}}
              as |field|
            >
              <field.Control />
            </customFields.Field>

            <customFields.Field
              @name="waze_forms_new_topic_button_label"
              @type="custom"
              @format="max"
              @title={{i18n "waze.forms.new_topic_button_label"}}
              as |field|
            >
              <field.Control>
                <ComboBox
                  @id="waze-forms-new-topic-button-label"
                  @content={{this.wazeFormsComposeButtonOptions}}
                  @value={{field.value}}
                  @onChange={{field.set}}
                  @options={{hash allowAny=false}}
                />
              </field.Control>
            </customFields.Field>

            <customFields.Field
              @name="enable_open_logs_button"
              @type="checkbox"
              @format="max"
              @title={{i18n "waze.forms.enable_open_logs_button"}}
              @onSet={{this.toggleEnableOpenLogsButton}}
              as |field|
            >
              <field.Control checked={{this.isOpenLogsButtonEnabled}} />
            </customFields.Field>

            {{#if this.isOpenLogsButtonEnabled}}
              <customFields.Field
                @name="groups_allowed_to_open_logs"
                @type="custom"
                @format="max"
                @title={{i18n "waze.forms.groups_allowed_to_open_logs"}}
                @description={{i18n
                  "waze.forms.groups_allowed_to_open_logs_description"
                }}
                as |field|
              >
                <field.Control>
                  <MultiSelect
                    @id="waze-forms-settings-groups-allowed-to-open-logs"
                    @content={{this.groups}}
                    @valueProperty={{null}}
                    @nameProperty={{null}}
                    @value={{this.groupsAllowedToOpenLogs}}
                    @onChange={{fn this.changeGroupsAllowedToOpenLogs field}}
                  />
                </field.Control>
              </customFields.Field>
            {{/if}}
          {{/if}}
        </form.Object>
      </form.Section>
    {{/let}}
  </template>
}
